import React from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "@tanstack/react-router";

import type { GroupDto } from "@synthesis/shared-services/auth/group/types";
import { createLocalProjectRole } from "@synthesis/shared-services/project-gge/role/client";
import { setMeta } from "@synthesis/shared-services/project-gge/projectMetaController/client";
import { IconClose, IconPlus } from "@synthesis/shared-icons";
import { Button, LoadingIndicator, Select, setSnack } from "@synthesis/shared-ui";

import { logger } from "@synthesis/shared-utils";

import { useQueryProject } from "../../../entity/project";
import { AccessDenied, PROJECT_ACCESS_DENIED_MESSAGE } from "../../components/access-denied";
import { useProjectAccessError, useUserRolesGGE } from "../../hooks";
import { useAssignedUsersQueryData, useGroupsQuery } from "./hooks/use-experts-queries";
import type { RoleAssignmentType } from "./list-of-experts-page.lib";
import { RoleTable } from "./local-components";

export const ListOfExpertsPage = ({
  usersForProject,
  groupsForProject,
}: {
  usersForProject?: React.MutableRefObject<RoleAssignmentType[] | null>;
  groupsForProject?: React.MutableRefObject<GroupDto[] | null>;
}) => {
  const methods = useForm();
  const { projectId } = useParams({ strict: false });
  const navigate = useNavigate();
  const { isGroupsLoading, allGroupsMap, groups, initialGroups } = useGroupsQuery(projectId);
  const { assignedUsersData, isAssignedUsersDataLoading } = useAssignedUsersQueryData(projectId);
  const { project, isLoading: isProjectLoading, isError, error: projectError } = useQueryProject(projectId);
  const isAccessError = useProjectAccessError(isError, projectError);
  const roles = useUserRolesGGE();

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedRoles, setSelectedRoles] = React.useState<RoleAssignmentType[]>([]);
  const [currentGroupId, setCurrentGroupId] = React.useState<string | null>(null);
  const [changedGroups, setChangedGroups] = React.useState<GroupDto[]>([]);
  const initialGroupsRef = React.useRef(initialGroups);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const [selectValues, setSelectValues] = React.useState<{ value: string; label: string }[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | null>(null);

  React.useEffect(() => {
    const values: { value: string; label: string }[] = [];
    groups.forEach((value) => {
      if (value.id && value.name) {
        values.push({ value: value.id, label: value.name });
      }
    });
    setSelectValues(values);
  }, [groups]);

  React.useEffect(() => {
    if (changedGroups.length > 0) {
      const filteredSelectValues = groups
        .filter((group) => group.id && !changedGroups.some((changedGroup) => changedGroup.id === group.id))
        .map((group) => ({ value: group.id!, label: group.name ?? "" }));
      setSelectValues(filteredSelectValues);
    }
  }, [changedGroups, groups]);

  const handleSelectGroup = (id: string) => {
    setSelectValue(id);
    const groupExists = allGroupsMap?.get(id);
    setCurrentGroupId(groupExists ? id : null);
  };

  const handleSelectRole = (role: RoleAssignmentType) => {
    const selectedRoleIndex = selectedRoles.findIndex((el) => el.id === role.id);
    if (selectedRoleIndex !== -1) {
      const arr = [...selectedRoles];
      arr[selectedRoleIndex] = role;
      setSelectedRoles(arr);
      if (usersForProject) {
        usersForProject.current = arr;
      }
    }
  };

  const handleAddRole = (id: string) => {
    setSelectedRoles((prevState) => {
      const updated = [...prevState, { id, userId: null }];
      if (usersForProject) {
        usersForProject.current = updated;
      }
      return updated;
    });
  };

  const handleDeleteRole = (id: string) => {
    setSelectedRoles((prevState) => {
      const updatedRoles = prevState.filter((el) => el.id !== id);
      if (usersForProject) {
        usersForProject.current = updatedRoles;
      }
      return updatedRoles;
    });
  };

  const onSubmit = async () => {
    if (projectId && changedGroups) {
      const hasDuplicateUserIds = new Set(selectedRoles.map((role) => role.userId)).size !== selectedRoles.length;
      if (hasDuplicateUserIds) {
        setSnack({
          title: "Выбраны одинаковые эксперты",
          messageType: "error",
        });
        return;
      }
      setIsLoading(true);
      try {
        if (selectedRoles.length > 0) {
          for (const role of selectedRoles) {
            if (role.userId) {
              await createLocalProjectRole({ path: { projectId }, body: { name: "Балансодержатель" }, query: { userId: role.userId } });
            }
          }
        }

        setSelectedRoles([]);
        const ids = changedGroups.map((group) => group.id).filter(Boolean) as string[];
        try {
          await setMeta({
            path: { projectId, key: "ownerGroup" },
            body: {
              key: "ownerGroup",
              value: JSON.stringify(ids),
            },
          });
        } catch (e) {
          logger.error(e);
        }

        setIsLoading(false);
        window.location.reload();
      } catch (error) {
        logger.error(error);
      }
    }
  };

  const stateGroups: GroupDto[] = React.useMemo(() => {
    if (groupsForProject) {
      groupsForProject.current = changedGroups;
    }
    return changedGroups;
  }, [changedGroups, groupsForProject]);

  React.useEffect(() => {
    if (!isInitialized && initialGroups.length > 0) {
      initialGroupsRef.current = initialGroups;
      setChangedGroups(initialGroups);
      setIsInitialized(true);
    } else if (isInitialized && initialGroups.length !== initialGroupsRef.current.length) {
      initialGroupsRef.current = initialGroups;
      setChangedGroups(initialGroups);
    }
  }, [initialGroups, isInitialized]);

  const handleAddGroup = () => {
    if (currentGroupId) {
      const newGroup = allGroupsMap?.get(currentGroupId);
      if (newGroup) {
        const isGroupAlreadyAdded = changedGroups.some((group) => group.id === newGroup.id);
        if (!isGroupAlreadyAdded) {
          setSelectValue(null);
          setChangedGroups([...changedGroups, newGroup]);
        }
      }
    }
  };

  const isArchived = projectId ? !!project?.isArchived : false;
  const isNotRight = !roles.СУПЕР && !roles.АДМИНИСТРИРОВАНИЕ;

  const handleDeleteGroup = (id: string) => {
    if (isArchived || isNotRight) {
      return;
    }
    setChangedGroups((prev) => (prev ? prev.filter((group) => group.id !== id) : []));
  };

  const handleCancel = () => {
    if (projectId) {
      navigate({
        to: isArchived ? "/archiveProject/$projectId/dashboard" : "/project/$projectId/dashboard",
        params: { projectId },
      });
    }
  };

  const loadingContent = assignedUsersData.length > 0 && (isGroupsLoading || isAssignedUsersDataLoading) ? <LoadingIndicator /> : <div />;

  if (isProjectLoading) {
    return (
      <div className="flex h-[calc(100vh-70px)] items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  if (isAccessError) {
    return <AccessDenied message={PROJECT_ACCESS_DENIED_MESSAGE} />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <main className="px-8 pb-6 pt-8">
          {projectId && <h1 className="heading-h4 pb-4">Список экспертов</h1>}
          <div className={`rounded-2xl bg-white ${projectId ? "px-6 pt-8" : ""}`}>
            <div className="flex flex-col gap-4">
              <h2 className="text-lg font-bold">Организации</h2>
              <span className="text-type-light">Организации, которые имеют доступ к проекту:</span>
              <div className="flex flex-wrap gap-4">
                {stateGroups && stateGroups.length > 0
                  ? stateGroups.map((group) => (
                      <div key={group.id} className="bg-additional-blue-90 text-additional-blue-50 flex items-center gap-2 rounded-lg px-3 py-1.5">
                        {group?.name}
                        <IconClose
                          tabIndex={0}
                          className="size-5 cursor-pointer outline-none"
                          onClick={() => group.id && handleDeleteGroup(group.id)}
                        />
                      </div>
                    ))
                  : loadingContent}
              </div>
              <div className="flex gap-4">
                <Select
                  size="md"
                  className="w-56"
                  isLoading={isGroupsLoading}
                  value={selectValue ?? ""}
                  items={selectValues}
                  onChange={handleSelectGroup}
                  disabled={isArchived || isNotRight}
                  placeholder="Выберите из списка"
                />
                <Button disabled={isArchived || isNotRight} onClick={handleAddGroup} leftIcon={<IconPlus className="size-6" />}>
                  Добавить организацию
                </Button>
              </div>
            </div>
            <div className="flex w-full flex-col">
              <main className="w-full pt-8">
                <div className="pb-4">
                  <RoleTable
                    assignedUsersData={assignedUsersData}
                    isAssignedUsersDataLoading={isAssignedUsersDataLoading}
                    handleSelectRole={handleSelectRole}
                    handleAddRole={handleAddRole}
                    handleDeleteRole={handleDeleteRole}
                    selectedRoles={selectedRoles}
                    projectId={projectId}
                    isGroupsLoading={isGroupsLoading}
                    isArchived={!!isArchived}
                    isNotRight={isNotRight}
                    selectGroupsValues={selectValues}
                  />
                </div>
              </main>
              {projectId && (
                <footer className="mb-4 flex justify-start gap-4 pt-4">
                  <Button theme="primary" type="submit" size="lg" disabled={isLoading || !!isArchived || isNotRight}>
                    {isLoading ? <LoadingIndicator className="text-white" /> : projectId ? "Применить изменения" : "Создать проект"}
                  </Button>
                  <Button theme="secondary" size="lg" disabled={isLoading || !!isArchived} onClick={handleCancel}>
                    {projectId ? "Отменить" : "Назад"}
                  </Button>
                </footer>
              )}
            </div>
          </div>
        </main>
      </form>
    </FormProvider>
  );
};
